import type { FC, ReactNode } from 'react';
import React, { useEffect } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';
import { useUserContext } from 'src/components/app/context/UserContext';

interface Message {
  id: string;
  color: Color;
  message: string;
  updated_at: string;
}

type Color = 'GREEN' | 'YELLOW' | 'RED';

enum messageVariantMap {
  GREEN = 'success',
  YELLOW = 'warning',
  RED = 'error',
}

const MaintenanceMessageContext = React.createContext({});

const MaintenanceMessageProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { i18n } = useTranslation();
  const { user } = useUserContext();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [cookies, setCookie] = useCookies();

  const endPoint = apiPaths.MAINTENANCE_MESSAGES;
  const loggedInUser = !!user;
  const { data, isFetching, isLoading } = useQuery<Message[], string>({
    queryKey: [endPoint, i18n.language, loggedInUser],
    enabled: loggedInUser,
    staleTime: 5 * 60 * 1000, // wait 5 min before allowing to fetch data again
    queryFn: () =>
      HttpService.get(endPoint)
        .then(({ data }) => data)
        .catch(() => Promise.reject()),
  });
  const loading = isFetching || isLoading;

  useEffect(() => {
    if (data && loggedInUser && !loading) {
      data.forEach(({ id, message, color, updated_at }) => {
        const messageId = `message-${id}`;
        if (cookies[messageId] !== updated_at) {
          closeSnackbar(messageId);
          enqueueSnackbar(message, {
            key: messageId,
            variant: messageVariantMap[color],
            persist: true,
            preventDuplicate: true,
            action: (snackbarId) => (
              <button
                onClick={() => {
                  setCookie(messageId, updated_at, {
                    secure: false,
                    path: '/',
                  });
                  closeSnackbar(snackbarId);
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'inherit',
                  cursor: 'pointer',
                  padding: '6px 8px',
                }}
              >
                Dismiss
              </button>
            ),
          });
        }
      });
    }
  }, [data, loading]);

  return (
    <MaintenanceMessageContext.Provider value={{}}>
      {children}
    </MaintenanceMessageContext.Provider>
  );
};

export default MaintenanceMessageProvider;
